// Invitación al quiz en la ficha de producto. Liviano a propósito: la ficha no
// carga el motor (quiz.js) cuando la invitación enlaza a la página del quiz.
//
// Hace cuatro cosas:
//   1. Mide la invitación: afh_quiz_entry_view (se vio) y afh_quiz_entry_click.
//      Es el denominador del embudo: sin él no hay tasa de inicio. Va al
//      dataLayer; en un artículo, también a GA4.
//   2. Una llamada de atención, una sola vez, cuando la invitación entra en
//      pantalla. No es un pulso en bucle: convive con «Agregar al carrito».
//   3. La barra anclada en móvil, si el bloque la activa.
//   4. Si la invitación lo activa, abre el quiz en una ventana sobre la ficha.
//      El motor (quiz.js, ~20 KB) y el catálogo llegan al primer clic en la
//      invitación, no al cargar la ficha: así no pesan en cada ficha.

(() => {
  if (window.__quizEntrada) return;
  window.__quizEntrada = true;

  const empujar = (evento, datos) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: `afh_${evento}`, ...datos });
  };
  const datosDe = (el) => ({
    quiz_id: el.dataset.quizEntry,
    product_id: el.dataset.quizProducto ? `shopify_${el.dataset.quizProducto}` : undefined,
  });
  const medir = (el, e, d = datosDe(el), a = el.dataset.quizArticulo) =>
    a ? import(el.dataset.quizTracking).then(() => window.quizMedir(e, { ...d, quiz_articulo: a }), () => empujar(e, d)) : empujar(e, d);
  const reducido = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  document.querySelectorAll('.quiz-entry').forEach((entry) => {
    if (entry.dataset.quizEntradaLista) return;
    entry.dataset.quizEntradaLista = '1';

    entry.querySelector('[data-quiz-entry-cta]')?.addEventListener('click', () => {
      if (entry.dataset.quizEntry) medir(entry, 'quiz_entry_click');
    });

    if (!('IntersectionObserver' in window)) return;
    const obs = new IntersectionObserver(
      (entradas) => {
        entradas.forEach((e) => {
          if (!e.isIntersecting) return;
          obs.disconnect();
          if (entry.dataset.quizEntry) medir(entry, 'quiz_entry_view');
          if (reducido || !entry.hasAttribute('data-quiz-cue')) return;
          // Un respiro tras aparecer: animar durante el desplazamiento no se ve.
          setTimeout(() => {
            entry.classList.add('is-cue');
            setTimeout(() => entry.classList.remove('is-cue'), 2600);
          }, 700);
        });
      },
      { threshold: 0.6 },
    );
    obs.observe(entry);
  });

  // Ventana de la ficha (snippets/quiz-invitacion.liquid). El quiz vive en su
  // página; al primer clic se trae esa página, se toma su sección y se monta
  // dentro de un <dialog>, sin salir del producto. Si algo falla, el botón es
  // un enlace y navega a la página del quiz: nunca queda muerto.
  // Se trae la página entera y no ?section_id=quiz: en una plantilla JSON esa
  // clave devuelve el archivo de la sección suelto y vacío, y el ID que sí
  // funciona (template--<n>__quiz) cambia con cada tema. 2026-09-28.
  const cargarQuiz = (dlg) =>
    (dlg.__quizCarga ||= (async () => {
      const r = await fetch(dlg.dataset.quizPagina, { credentials: 'same-origin' });
      if (!r.ok) throw new Error(`la página del quiz respondió ${r.status}`);
      const doc = new DOMParser().parseFromString(await r.text(), 'text/html');
      const raiz = doc.querySelector('[data-quiz]');
      if (!raiz?.querySelector('[data-quiz-config]')) throw new Error('la respuesta no trae el quiz');

      // Estilos que trae la sección (pdp.css, colores de Ajustes del tema,
      // quiz.css): una landing propia no los carga. Se espera a que lleguen
      // para no pintar el quiz sin formato.
      const seccion = raiz.closest('[id^="shopify-section"]') || raiz;
      const esperas = [];
      seccion.querySelectorAll('link[rel="stylesheet"], style').forEach((n) => {
        if (n.tagName === 'LINK') {
          const href = n.getAttribute('href');
          if ([...document.styleSheets].some((s) => s.href && href && s.href.endsWith(href.replace(/^https?:/, '')))) return;
          const l = document.importNode(n, true);
          esperas.push(new Promise((ok) => { l.onload = l.onerror = ok; setTimeout(ok, 3000); }));
          document.head.append(l);
        } else document.head.append(document.importNode(n, true));
      });

      // La medición y el destino del lead escuchan en document: se cargan
      // antes que el motor para no perder quiz_view. Una URL ya importada por
      // la página no se ejecuta dos veces (mapa de módulos del navegador).
      const extras = (dlg.dataset.quizMedicion || '').split(',').filter(Boolean);
      await Promise.all([...esperas, ...extras.map((u) => import(u).catch((e) => console.warn('[quiz]', e)))]);

      const nuevo = document.importNode(raiz, true);
      nuevo.classList.add('quiz--dialogo');
      // Presentación destacada (ventana ancha con los estilos de la marca): la
      // decide la ficha, no la página del quiz, así que llega en el <dialog>.
      (dlg.dataset.quizClases || '').split(/\s+/).filter(Boolean).forEach((c) => nuevo.classList.add(c));
      nuevo.style.removeProperty('--quiz-top-offset');
      nuevo.dataset.quizDesde = dlg.dataset.quizDesde || '';
      if (dlg.dataset.quizArticulo) nuevo.dataset.quizArticulo = dlg.dataset.quizArticulo;
      dlg.querySelector('[data-quiz-cuerpo]').replaceChildren(nuevo);

      const motor = await import(dlg.dataset.quizMotor);
      motor.montar?.(nuevo);
      return nuevo;
    })().catch((e) => {
      dlg.__quizCarga = null;
      throw e;
    }));

  document.querySelectorAll('[data-quiz-ventana]').forEach((cta) => {
    const dlg = document.getElementById(cta.dataset.quizVentana);
    // Sin <dialog> nativo, el enlace lleva a la página del quiz.
    if (!dlg || typeof dlg.showModal !== 'function' || dlg.dataset.quizVentanaLista) return;
    dlg.dataset.quizVentanaLista = '1';
    if (dlg.dataset.quizArticulo) {
      document.body.append(dlg);
      dlg.addEventListener('quiz:add_to_cart', () => dlg.close());
    }
    const cuerpo = dlg.querySelector('[data-quiz-cuerpo]');
    const clave = { quizVentana: dlg.id };

    const cerrar = () => dlg.open && dlg.close();
    dlg.querySelector('[data-quiz-close]')?.addEventListener('click', cerrar);
    // Clic en el fondo: el clic llega al propio <dialog> cuando cae fuera de su caja.
    dlg.addEventListener('click', (e) => {
      if (e.target !== dlg) return;
      const c = dlg.getBoundingClientRect();
      if (e.clientX < c.left || e.clientX > c.right || e.clientY < c.top || e.clientY > c.bottom) cerrar();
    });
    dlg.addEventListener('close', () => {
      document.documentElement.style.overflow = '';
      // Se cerró con la X, el fondo o Escape: se retira la entrada que puso
      // la apertura, para que atrás siga saliendo de la ficha.
      if (history.state?.quizVentana === dlg.id) history.back();
      dlg.__quizOpener?.focus({ preventScroll: true });
    });
    // En móvil, atrás es el gesto natural para cerrar la ventana.
    window.addEventListener('popstate', () => {
      if (dlg.open && history.state?.quizVentana !== dlg.id) cerrar();
    });

    cta.addEventListener('click', async (evento) => {
      evento.preventDefault();
      dlg.__quizOpener = cta;
      if (!cuerpo.firstElementChild) {
        cuerpo.innerHTML = '<p class="quiz-dialog__cargando" role="status">Preparando tu cuestionario…</p>';
      }
      dlg.showModal();
      document.documentElement.style.overflow = 'hidden';
      try {
        history.pushState(clave, '');
      } catch {
        /* sin pushState: atrás sale de la ficha, como antes */
      }
      try {
        const raiz = await cargarQuiz(dlg);
        if (!dlg.open) return;
        raiz.querySelector('button, a')?.focus();
        raiz.dispatchEvent(new CustomEvent('quiz:open', { bubbles: true, detail: { id: raiz.__quiz?.cfg?.id, desde: dlg.dataset.quizOrigen || 'producto' } }));
      } catch (error) {
        console.warn('[quiz] no se pudo abrir en ventana; se abre su página:', error);
        window.location.href = dlg.dataset.quizRespaldo || cta.href;
      }
    });
  });

  // Modo ventana: el motor se importa al pulsar. Al terminar la importación,
  // quiz.js ya registró su propio manejador de apertura y se repite el clic.
  let motor = null;
  document.querySelectorAll('[data-quiz-open][data-quiz-motor]').forEach((boton) => {
    boton.addEventListener('click', async (evento) => {
      if (window.__quizMotorListo) return;
      evento.preventDefault();
      evento.stopImmediatePropagation();
      motor = motor || import(boton.dataset.quizMotor);
      await motor;
      window.__quizMotorListo = true;
      boton.click();
    });
  });

  // Barra anclada en móvil: aparece al pasar la invitación y se puede cerrar.
  // Una vez cerrada no vuelve en la sesión.
  document.querySelectorAll('[data-quiz-bar]').forEach((bar) => {
    const clave = 'quiz-bar-cerrada';
    let cerrada = false;
    try {
      cerrada = sessionStorage.getItem(clave) === '1';
    } catch {
      /* modo privado: se comporta como no cerrada */
    }
    const ancla = document.querySelector(bar.dataset.quizBarAfter || '.quiz-entry');
    if (cerrada || !ancla || !('IntersectionObserver' in window)) return bar.remove();

    const obs = new IntersectionObserver(
      (entradas) => {
        entradas.forEach((e) => bar.classList.toggle('is-on', !e.isIntersecting && e.boundingClientRect.top < 0));
      },
      { threshold: 0 },
    );
    obs.observe(ancla);

    bar.querySelector('[data-quiz-bar-close]')?.addEventListener('click', () => {
      bar.classList.remove('is-on');
      obs.disconnect();
      try {
        sessionStorage.setItem(clave, '1');
      } catch {
        /* sin sessionStorage: solo se oculta en esta vista */
      }
      setTimeout(() => bar.remove(), 400);
    });
  });
})();
